import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, BookOpen, Loader2, Trash2, ShieldAlert, Wand2, Share2, Lock,
  Users, MessageSquare, AlertTriangle, Lightbulb, ScrollText, Check, Copy, X,
  ThumbsUp, MinusCircle, Sparkles, Eye, EyeOff, Calendar as CalendarIcon,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const NOTE_TYPES = [
  { value: "praise", label: "Praise", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "constructive", label: "Constructive", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "observation", label: "Observation", color: "bg-slate-100 text-slate-700 border-slate-200" },
  { value: "one_on_one_takeaway", label: "1:1 takeaway", color: "bg-violet-50 text-violet-700 border-violet-200" },
  { value: "stakeholder_input", label: "Stakeholder input", color: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  { value: "risk", label: "Risk / concern", color: "bg-rose-50 text-rose-700 border-rose-200" },
  { value: "growth_idea", label: "Growth idea", color: "bg-blue-50 text-blue-700 border-blue-200" },
];
const TYPE_BY_VAL = Object.fromEntries(NOTE_TYPES.map((t) => [t.value, t]));

const SENTIMENTS = [
  { value: "positive", label: "Positive", icon: ThumbsUp, color: "text-emerald-600" },
  { value: "neutral", label: "Neutral", icon: MinusCircle, color: "text-slate-500" },
  { value: "developmental", label: "Developmental", icon: AlertTriangle, color: "text-amber-600" },
];
const SENT_BY_VAL = Object.fromEntries(SENTIMENTS.map((s) => [s.value, s]));

function TypeChip({ value }) {
  const t = TYPE_BY_VAL[value] || TYPE_BY_VAL.observation;
  return (
    <span className={cn("inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border font-medium", t.color)}>
      {t.label}
    </span>
  );
}
function SentimentDot({ value }) {
  const s = SENT_BY_VAL[value] || SENT_BY_VAL.neutral;
  const Icon = s.icon;
  return (
    <span className={cn("inline-flex items-center gap-1 text-[11px]", s.color)}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

export default function CoachingLog() {
  const [reports, setReports] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [synthOpen, setSynthOpen] = useState(false);
  const [activeGoals, setActiveGoals] = useState([]);
  const [bundle, setBundle] = useState(null);

  const reloadReports = async () => {
    setLoadingReports(true);
    try {
      const r = await api.get("/coaching/reports");
      setReports(r.data.items || []);
      if (!activeId && r.data.items?.[0]) {
        setActiveId(r.data.items[0].employee.id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load reports");
    } finally {
      setLoadingReports(false);
    }
  };

  const reloadNotes = async (eid = activeId) => {
    if (!eid) return;
    setLoadingNotes(true);
    try {
      const [n, b] = await Promise.all([
        api.get("/coaching/notes", { params: { subject_employee_id: eid } }),
        api.get("/coaching/one-on-one-bundle", { params: { subject_employee_id: eid } }),
      ]);
      setNotes(n.data.items || []);
      setBundle(b.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load notes");
    } finally {
      setLoadingNotes(false);
    }
  };

  const loadGoalsForActive = async () => {
    try {
      const cy = await api.get("/cycles/active/me");
      const items = cy.data.items || [];
      const cycleId = items?.[0]?.cycle?.id;
      if (cycleId && activeId) {
        const r = await api.get("/objectives", { params: { cycle_id: cycleId, employee_id: activeId } });
        setActiveGoals(r.data.items || []);
      } else {
        setActiveGoals([]);
      }
    } catch {
      setActiveGoals([]);
    }
  };

  useEffect(() => { reloadReports(); }, []);
  useEffect(() => { if (activeId) { reloadNotes(activeId); loadGoalsForActive(); } }, [activeId]);

  const activeReport = useMemo(() => reports.find((r) => r.employee.id === activeId), [reports, activeId]);

  const sentimentCounts = useMemo(() => {
    const c = { positive: 0, neutral: 0, developmental: 0 };
    notes.forEach((n) => { c[n.sentiment] = (c[n.sentiment] || 0) + 1; });
    return c;
  }, [notes]);

  if (!loadingReports && reports.length === 0) {
    return (
      <div className="max-w-3xl mx-auto py-16 text-center" data-testid="coaching-empty-no-reports">
        <div className="w-12 h-12 rounded-full bg-slate-900 text-white inline-flex items-center justify-center mb-4">
          <BookOpen className="w-6 h-6" />
        </div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Coaching Log</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
          A private notebook for the feedback you'd otherwise forget by review time. This space is
          available once you have direct reports — none are mapped to you yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl" data-testid="coaching-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="axia-overline">Coaching Log · private notebook</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">
            One running record per report.
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl flex items-center gap-2">
            <Lock className="w-3.5 h-3.5 text-slate-400" />
            Strictly private. Reports never see notes unless you explicitly share a redacted version.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left rail — reports */}
        <aside className="lg:col-span-3 space-y-1" data-testid="coaching-reports-rail">
          <div className="axia-overline px-1 mb-1">My Direct Reports</div>
          {reports.map((r) => {
            const e = r.employee;
            const isActive = r.employee.id === activeId;
            return (
              <button
                key={e.id}
                onClick={() => setActiveId(e.id)}
                className={cn(
                  "w-full text-left rounded-lg p-3 transition border",
                  isActive
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50",
                )}
                data-testid={`report-pick-${e.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0",
                    isActive ? "bg-white text-slate-900" : "bg-slate-900 text-white",
                  )}>
                    {(e.first_name?.[0] || "?") + (e.last_name?.[0] || "")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{e.first_name} {e.last_name}</div>
                    <div className={cn("text-[11px] truncate", isActive ? "text-slate-300" : "text-slate-500")}>
                      {e.designation || e.department || "—"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-2.5 text-[11px]">
                  <span className={cn(isActive ? "text-slate-300" : "text-slate-500")}>
                    {r.note_count} note{r.note_count === 1 ? "" : "s"}
                  </span>
                  {r.days_since_last_note != null && (
                    <span className={cn(
                      r.days_since_last_note > 30
                        ? (isActive ? "text-amber-300" : "text-amber-600")
                        : (isActive ? "text-slate-300" : "text-slate-400"),
                    )}>
                      {r.days_since_last_note}d ago
                    </span>
                  )}
                  {r.note_count === 0 && (
                    <span className={cn(isActive ? "text-amber-300" : "text-amber-600")}>none yet</span>
                  )}
                </div>
              </button>
            );
          })}
        </aside>

        {/* Main pane */}
        <main className="lg:col-span-9 space-y-5" data-testid="coaching-main">
          {activeReport && (
            <>
              <div className="flex items-start justify-between flex-wrap gap-3 axia-card p-5">
                <div>
                  <div className="font-display text-xl font-semibold text-slate-900">
                    Notes for {activeReport.employee.first_name} {activeReport.employee.last_name}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {activeReport.employee.designation || activeReport.employee.department}
                    {bundle?.next_meeting?.scheduled_for && (
                      <span className="ml-2 inline-flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" />
                        Next 1:1 {bundle.next_meeting.scheduled_for.slice(0, 10)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    <SentimentBar c={sentimentCounts} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => setSynthOpen(true)} disabled={!notes.length} data-testid="synthesize-btn">
                    <ScrollText className="w-4 h-4 mr-2" /> Synthesize for review
                  </Button>
                  <Button onClick={() => setCreateOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="add-note-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add note
                  </Button>
                </div>
              </div>

              {/* 1:1 prep bundle */}
              {bundle && bundle.items?.length > 0 && bundle.since && (
                <section className="rounded-lg border border-violet-200 bg-violet-50/40 p-4" data-testid="ono-bundle">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-violet-700" />
                    <div className="text-sm font-semibold text-violet-900">
                      Bring to your next 1:1 — {bundle.items.length} note{bundle.items.length > 1 ? "s" : ""} since {bundle.since}
                    </div>
                  </div>
                  <p className="text-xs text-violet-800/80">Tap any note below to review or share a redacted version with your report ahead of the meeting.</p>
                </section>
              )}

              {/* Notes timeline */}
              <section className="space-y-3" data-testid="coaching-notes-list">
                {loadingNotes ? (
                  <div className="text-sm text-slate-400 text-center py-12">Loading…</div>
                ) : notes.length === 0 ? (
                  <NoteEmpty onCreate={() => setCreateOpen(true)} />
                ) : (
                  notes.map((n) => (
                    <NoteRow key={n.id} note={n} onOpen={() => setEditing(n)} />
                  ))
                )}
              </section>
            </>
          )}
        </main>
      </div>

      <CreateNoteDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        subjectEmployeeId={activeId}
        activeGoals={activeGoals}
        onCreated={() => { reloadNotes(); reloadReports(); }}
      />

      <NoteDetailDialog
        note={editing}
        onClose={() => setEditing(null)}
        activeGoals={activeGoals}
        onChanged={() => { reloadNotes(); reloadReports(); }}
        onDeleted={() => { setEditing(null); reloadNotes(); reloadReports(); }}
      />

      <SynthesizeDialog
        open={synthOpen}
        onOpenChange={setSynthOpen}
        subjectEmployeeId={activeId}
        subjectName={activeReport ? `${activeReport.employee.first_name} ${activeReport.employee.last_name}` : ""}
        notes={notes}
      />
    </div>
  );
}

function SentimentBar({ c }) {
  const total = (c.positive || 0) + (c.neutral || 0) + (c.developmental || 0);
  if (!total) return <span className="text-xs text-slate-400">No notes yet</span>;
  const pct = (n) => Math.round((n / total) * 100);
  return (
    <div className="flex-1 max-w-md" data-testid="sentiment-bar">
      <div className="h-2 rounded-full overflow-hidden bg-slate-100 flex">
        <div className="h-full bg-emerald-400" style={{ width: `${pct(c.positive)}%` }} title={`Positive ${c.positive}`} />
        <div className="h-full bg-slate-300" style={{ width: `${pct(c.neutral)}%` }} title={`Neutral ${c.neutral}`} />
        <div className="h-full bg-amber-400" style={{ width: `${pct(c.developmental)}%` }} title={`Developmental ${c.developmental}`} />
      </div>
      <div className="flex items-center justify-between text-[10px] text-slate-500 mt-1.5">
        <span>{c.positive} positive</span>
        <span>{c.neutral} neutral</span>
        <span>{c.developmental} developmental</span>
      </div>
    </div>
  );
}

function NoteEmpty({ onCreate }) {
  return (
    <div className="axia-card p-12 text-center" data-testid="notes-empty">
      <div className="w-12 h-12 rounded-full bg-violet-50 text-violet-600 inline-flex items-center justify-center mb-4">
        <BookOpen className="w-6 h-6" />
      </div>
      <div className="font-display text-lg font-semibold text-slate-900">No notes yet</div>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
        A two-line note today saves an hour of recall in December. Capture observations, praise, and growth ideas as they happen.
      </p>
      <Button onClick={onCreate} className="mt-5 bg-slate-900 hover:bg-slate-800" data-testid="empty-add-note-btn">
        <Plus className="w-4 h-4 mr-2" /> Add your first note
      </Button>
    </div>
  );
}

function NoteRow({ note, onOpen }) {
  return (
    <div
      onClick={onOpen}
      className="axia-card p-4 hover:border-slate-300 cursor-pointer transition group"
      data-testid={`note-row-${note.id}`}
    >
      <div className="flex items-center gap-2 flex-wrap mb-1.5">
        <TypeChip value={note.type} />
        <SentimentDot value={note.sentiment} />
        <span className="text-[11px] text-slate-400 font-mono">{note.observed_at}</span>
        {note.privacy === "shared" ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-blue-700 bg-blue-50 rounded px-2 py-0.5">
            <Eye className="w-3 h-3" /> Shared with report
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 bg-slate-50 rounded px-2 py-0.5">
            <Lock className="w-3 h-3" /> Private
          </span>
        )}
        {(note.ai_bias_flags || []).length > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] text-rose-700 bg-rose-50 rounded px-2 py-0.5">
            <ShieldAlert className="w-3 h-3" /> {note.ai_bias_flags.length} bias flag{note.ai_bias_flags.length > 1 ? "s" : ""}
          </span>
        )}
      </div>
      <p className="text-sm text-slate-700 leading-relaxed line-clamp-3">{note.body}</p>
      {note.source_label && (
        <div className="text-[11px] text-slate-400 mt-2">Source: {note.source_label}</div>
      )}
    </div>
  );
}

/* ---------------- Create Dialog ---------------- */
function CreateNoteDialog({ open, onOpenChange, subjectEmployeeId, activeGoals, onCreated }) {
  const [type, setType] = useState("observation");
  const [sentiment, setSentiment] = useState("neutral");
  const [body, setBody] = useState("");
  const [observedAt, setObservedAt] = useState("");
  const [sourceLabel, setSourceLabel] = useState("");
  const [linked, setLinked] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setType("observation"); setSentiment("neutral"); setBody("");
      setObservedAt(new Date().toISOString().slice(0, 10));
      setSourceLabel(""); setLinked([]);
    }
  }, [open]);

  const submit = async () => {
    if (!body.trim()) return toast.error("Body required");
    setSaving(true);
    try {
      await api.post("/coaching/notes", {
        subject_employee_id: subjectEmployeeId,
        type, sentiment, body, observed_at: observedAt,
        source_label: sourceLabel || null,
        linked_objective_ids: linked,
      });
      toast.success("Note saved");
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="create-note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BookOpen className="w-4 h-4" /> Add coaching note</DialogTitle>
          <DialogDescription>Strictly private. Capture what you observed, when, and how it landed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="note-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {NOTE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Sentiment</Label>
              <Select value={sentiment} onValueChange={setSentiment}>
                <SelectTrigger data-testid="note-sentiment-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SENTIMENTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">What happened?</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              placeholder="A specific situation, what they did, and the impact. Keep it factual."
              data-testid="note-body-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Observed on</Label>
              <Input type="date" value={observedAt} onChange={(e) => setObservedAt(e.target.value)} data-testid="note-date-input" />
            </div>
            <div>
              <Label className="text-xs">Source <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input
                value={sourceLabel}
                onChange={(e) => setSourceLabel(e.target.value)}
                placeholder="e.g. 1:1 on May 3"
                data-testid="note-source-input"
              />
            </div>
          </div>
          {activeGoals.length > 0 && (
            <div>
              <Label className="text-xs">Tie to goals <span className="text-slate-400 font-normal">(optional)</span></Label>
              <div className="mt-1.5 space-y-1.5 max-h-32 overflow-y-auto rounded-md border border-slate-200 p-2">
                {activeGoals.map((g) => {
                  const checked = linked.includes(g.id);
                  return (
                    <label key={g.id} className="flex items-start gap-2 text-sm cursor-pointer hover:bg-slate-50 px-1.5 py-1 rounded" data-testid={`note-link-goal-${g.id}`}>
                      <input type="checkbox" checked={checked} onChange={(e) => setLinked((p) => e.target.checked ? [...p, g.id] : p.filter((x) => x !== g.id))} className="mt-0.5" />
                      <span className="text-slate-700 line-clamp-2">{g.fields?.statement || g.fields?.objective || "Goal"}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-create-note">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="save-note-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Save note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Note Detail (bias, reframe, share, delete) ---------------- */
function NoteDetailDialog({ note, onClose, activeGoals, onChanged, onDeleted }) {
  const [current, setCurrent] = useState(note);
  const [biasing, setBiasing] = useState(false);
  const [bias, setBias] = useState(null);
  const [reframing, setReframing] = useState(false);
  const [reframed, setReframed] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [shareBody, setShareBody] = useState("");
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    setCurrent(note);
    setBias(null); setReframed(null);
    setShareBody(note?.body || "");
    setShareOpen(false);
  }, [note?.id]);

  const refresh = async () => {
    if (!current?.id) return;
    try {
      const r = await api.get(`/coaching/notes/${current.id}`);
      setCurrent(r.data);
    } catch { /* ignore */ }
  };

  if (!current) return null;
  const n = current; // alias used below

  const biasCheck = async () => {
    setBiasing(true);
    try {
      const r = await api.post(`/coaching/notes/${n.id}/bias-check`);
      setBias(r.data);
      await refresh();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    } finally {
      setBiasing(false);
    }
  };

  const reframe = async () => {
    setReframing(true);
    try {
      const r = await api.post(`/coaching/notes/${n.id}/reframe`);
      setReframed(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    } finally {
      setReframing(false);
    }
  };

  const applyReframe = async () => {
    if (!reframed?.rewritten) return;
    await api.patch(`/coaching/notes/${n.id}`, { body: reframed.rewritten });
    toast.success("Reframed text applied");
    setReframed(null);
    await refresh();
    onChanged?.();
  };

  const doShare = async () => {
    if (!shareBody.trim()) return toast.error("Write the version you want to share");
    setSharing(true);
    try {
      await api.post(`/coaching/notes/${n.id}/share`, { shared_body: shareBody });
      toast.success("Shared with the report");
      setShareOpen(false);
      await refresh();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to share");
    } finally {
      setSharing(false);
    }
  };

  const unshare = async () => {
    await api.post(`/coaching/notes/${n.id}/unshare`);
    toast.success("No longer shared");
    await refresh();
    onChanged?.();
  };

  const remove = async () => {
    if (!confirm("Delete this note? This cannot be undone.")) return;
    await api.delete(`/coaching/notes/${n.id}`);
    toast.success("Deleted");
    onDeleted?.();
  };

  return (
    <Dialog open={!!current} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="note-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <TypeChip value={n.type} />
            <SentimentDot value={n.sentiment} />
            <span className="text-[11px] text-slate-400 font-mono">{n.observed_at}</span>
            {n.privacy === "shared" ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-blue-700 bg-blue-50 rounded px-2 py-0.5"><Eye className="w-3 h-3" /> Shared</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 bg-slate-50 rounded px-2 py-0.5"><Lock className="w-3 h-3" /> Private</span>
            )}
          </div>
          <DialogTitle className="font-display text-base">Coaching note</DialogTitle>
        </DialogHeader>

        <section className="rounded-lg border border-slate-200 p-4 bg-slate-50/40">
          <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{n.body}</p>
          {n.source_label && (
            <div className="text-[11px] text-slate-500 mt-2">Source: {n.source_label}</div>
          )}
        </section>

        {/* AI tools */}
        <section className="rounded-lg border border-slate-200 p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-2">AI tools</div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={biasCheck} disabled={biasing} data-testid="bias-check-btn">
              {biasing ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <ShieldAlert className="w-3 h-3 mr-2" />} Bias check
            </Button>
            <Button size="sm" variant="outline" onClick={reframe} disabled={reframing} data-testid="reframe-btn">
              {reframing ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Wand2 className="w-3 h-3 mr-2" />} Reframe (SBI)
            </Button>
          </div>
          {bias && (
            <div className="mt-3 space-y-2">
              <div className="text-[11px] uppercase text-slate-500">Risk: <span className={cn(
                bias.overall_risk === "high" ? "text-rose-700"
                  : bias.overall_risk === "medium" ? "text-amber-700"
                    : "text-emerald-700",
              )}>{bias.overall_risk}</span></div>
              {(bias.flags || []).map((f, i) => (
                <div key={i} className="text-xs bg-rose-50/60 border border-rose-100 rounded p-2" data-testid={`bias-flag-${i}`}>
                  <div><strong className="text-rose-800">{f.type}</strong> · "{f.phrase}"</div>
                  <div className="text-slate-600 mt-1">{f.rationale}</div>
                  <div className="text-emerald-700 mt-1">→ {f.suggestion}</div>
                </div>
              ))}
              {(!bias.flags || bias.flags.length === 0) && (
                <div className="text-xs text-emerald-700">No bias flags detected.</div>
              )}
            </div>
          )}
          {reframed && (
            <div className="mt-3 space-y-2">
              <div className="bg-violet-50/40 border border-violet-100 rounded p-3 text-sm text-slate-800 whitespace-pre-wrap">{reframed.rewritten}</div>
              {reframed.delivery_tip && (
                <div className="text-xs text-slate-600"><strong>Delivery tip:</strong> {reframed.delivery_tip}</div>
              )}
              {(reframed.label_flags || []).length > 0 && (
                <div className="text-xs text-slate-600">
                  <div className="font-medium mb-1">Behaviour swaps:</div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {reframed.label_flags.map((f, i) => (
                      <li key={i}>"{f.phrase}" → {f.replacement}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button size="sm" variant="ghost" onClick={() => setReframed(null)}>Dismiss</Button>
                <Button size="sm" onClick={applyReframe} data-testid="apply-reframe-btn"><Check className="w-3 h-3 mr-1" /> Replace body</Button>
              </div>
            </div>
          )}
        </section>

        {/* Sharing */}
        <section className="rounded-lg border border-slate-200 p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-2">Share with the report</div>
          {n.privacy === "shared" ? (
            <div className="space-y-2">
              <div className="bg-blue-50/40 border border-blue-100 rounded p-3 text-sm text-slate-800">
                <div className="text-[11px] uppercase tracking-wider text-blue-700 font-semibold mb-1">Shared body</div>
                <div className="whitespace-pre-wrap">{n.shared_body}</div>
              </div>
              <Button size="sm" variant="outline" onClick={unshare} data-testid="unshare-btn"><EyeOff className="w-3 h-3 mr-1" /> Unshare</Button>
            </div>
          ) : !shareOpen ? (
            <Button size="sm" variant="outline" onClick={() => setShareOpen(true)} data-testid="open-share-btn">
              <Share2 className="w-3 h-3 mr-2" /> Share a redacted version…
            </Button>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-slate-500">Edit the body below — your raw private note stays private. Only this version is visible to the report.</p>
              <Textarea value={shareBody} onChange={(e) => setShareBody(e.target.value)} rows={4} data-testid="share-body-input" />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setShareOpen(false)}>Cancel</Button>
                <Button size="sm" onClick={doShare} disabled={sharing} data-testid="confirm-share-btn">
                  {sharing ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Share2 className="w-3 h-3 mr-2" />} Share
                </Button>
              </div>
            </div>
          )}
        </section>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={remove} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid="delete-note-btn">
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
          <Button variant="outline" onClick={onClose} data-testid="close-detail-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Synthesize ---------------- */
function SynthesizeDialog({ open, onOpenChange, subjectEmployeeId, subjectName, notes }) {
  const [running, setRunning] = useState(false);
  const [out, setOut] = useState(null);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (open) {
      const init = {};
      notes.forEach((n) => { init[n.id] = true; });
      setSelected(init);
      setOut(null);
    }
  }, [open, notes]);

  const ids = Object.keys(selected).filter((k) => selected[k]);

  const run = async () => {
    if (!ids.length) return toast.error("Pick at least one note");
    setRunning(true);
    try {
      const r = await api.post("/coaching/synthesize", { subject_employee_id: subjectEmployeeId, note_ids: ids });
      setOut(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    } finally {
      setRunning(false);
    }
  };

  const copy = async () => {
    if (!out?.narrative) return;
    await navigator.clipboard.writeText(out.narrative);
    toast.success("Copied");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="synthesize-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ScrollText className="w-4 h-4" /> Synthesize for review · {subjectName}</DialogTitle>
          <DialogDescription>We'll group your notes by goal/theme into a manager review draft. Edit before submitting.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2 border border-slate-200 rounded-lg p-3 max-h-72 overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Notes ({ids.length}/{notes.length})</span>
              <button
                className="text-xs text-slate-500 hover:text-slate-900"
                onClick={() => {
                  const all = ids.length === notes.length;
                  const next = {};
                  notes.forEach((n) => { next[n.id] = !all; });
                  setSelected(next);
                }}
                data-testid="synth-toggle-all-btn"
              >{ids.length === notes.length ? "Clear" : "Select all"}</button>
            </div>
            {notes.map((n) => (
              <label key={n.id} className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer" data-testid={`synth-pick-${n.id}`}>
                <input type="checkbox" checked={!!selected[n.id]} onChange={(e) => setSelected((p) => ({ ...p, [n.id]: e.target.checked }))} className="mt-0.5" />
                <span className="text-xs text-slate-700 line-clamp-2">{n.body}</span>
              </label>
            ))}
          </div>
          <div className="lg:col-span-3 space-y-3">
            <Button onClick={run} disabled={running || !ids.length} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="run-synth-btn">
              {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Generate appraisal draft
            </Button>
            {out && (
              <>
                {out.balance && (
                  <div className="text-xs text-slate-500 flex items-center gap-3">
                    <span><strong className="text-emerald-700">{out.balance.positive}</strong> positive</span>
                    <span><strong className="text-slate-700">{out.balance.neutral}</strong> neutral</span>
                    <span><strong className="text-amber-700">{out.balance.developmental}</strong> developmental</span>
                  </div>
                )}
                <div className="rounded-lg border border-slate-200 bg-slate-50/50">
                  <div className="flex items-center justify-between p-2 border-b border-slate-200">
                    <span className="text-xs text-slate-500">Manager-review draft</span>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={copy} data-testid="copy-synth-btn">
                      <Copy className="w-3 h-3 mr-1" /> Copy
                    </Button>
                  </div>
                  <pre className="text-sm text-slate-800 whitespace-pre-wrap p-4 font-sans leading-relaxed">{out.narrative}</pre>
                </div>
                {(out.themes || []).length > 0 && (
                  <div className="text-xs text-slate-700">
                    <div className="font-medium mb-1">Themes</div>
                    <ul className="space-y-1">
                      {out.themes.map((t, i) => (
                        <li key={i} className="bg-white border border-slate-200 rounded p-2">
                          <strong>{t.label}</strong> <span className="text-slate-400">· {t.mentions} mention{t.mentions !== 1 ? "s" : ""}</span>
                          <div className="text-slate-600">{t.summary}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(out.coverage_gaps || []).length > 0 && (
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded p-2.5">
                    <div className="font-medium mb-1">Coverage to consider:</div>
                    <ul className="list-disc pl-5">
                      {out.coverage_gaps.map((g, i) => <li key={i}>{g}</li>)}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-synth-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
